/**
 *
 * @file index.tsx
 *
 * @fileoverview Component that renders the main navigation.
 * This component receives a full menu and renders it as links.
 * It also renders the user menu next to the links.
 */

import React, { useContext } from 'react';
import dynamic from 'next/dynamic';
import { Menu } from '~/jobiqo/models/navigation';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { FormattedMessage } from 'react-intl';
import LinkDesktop from '~/jobiqo/components/Header/components/Link/Desktop';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import { HiddenVisually as VisuallyHidden } from '~/vcl/atoms/14-w-a11y/02-visually-hidden';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuTrigger,
} from '~/vcl/organisms/07-navigation/04-desktop-menu';

const UserNavigation = dynamic(
  import('~/jobiqo/components/Header/components/UserNavigation'),
  {
    ssr: false,
  },
);

interface DesktopMainNavigationProps {
  path: string;
  querystring: any;
  menu: Menu;
  userMenu: Menu;
}

export default function DesktopMainNavigation({
  path,
  menu,
  userMenu,
}: DesktopMainNavigationProps) {
  const userContext: UserContextInterface = useContext(UserContext);

  return (
    <AlignedCenter flow="row">
      <VisuallyHidden id="header-navigation">
        <FormattedMessage
          id="header-navigation"
          defaultMessage="Header navigation"
        />
      </VisuallyHidden>
      {menu && (
        <NavigationMenu aria-labelledby="header-navigation">
          {menu.items.map((link) => {
            return (
              <NavigationMenuItem key={`menu-${link.title}`}>
                {
                  // If there are children use navigation menu which renders links.
                  // Otherwise, we can just display the link.
                  !!link.children.length ? (
                    <>
                      <NavigationMenuTrigger>
                        {link.title}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        {link.children.map((link) => (
                          <LinkDesktop
                            key={`${link.url.path}--${link.title}`}
                            link={link}
                            path={path}
                          />
                        ))}
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <LinkDesktop
                      key={`${link.url.path}--${link.title}`}
                      link={link}
                      path={path}
                    />
                  )
                }
              </NavigationMenuItem>
            );
          })}
        </NavigationMenu>
      )}
      {userContext.user && (
        <div style={{ marginLeft: '0.5rem' }}>
          <UserNavigation userMenu={userMenu} />
        </div>
      )}
    </AlignedCenter>
  );
}
